"use client";

import React from "react";
import { useGrid } from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { customSanitize } from "@/utils/customSanitize";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { CategoryOverviewPages } from "@/getData/getPostGridData";
import styles from "./GridCategoryFilter.module.scss";

interface GridCategoryFilterProps {
  categories: CategoryOverviewPages[];
  activeCategoryId: number | null;
  onCategoryChange: (categoryId: number | null) => void;
  className?: string;
}

export const GridCategoryFilter = ({
  categories,
  activeCategoryId,
  onCategoryChange,
  className = "",
}: GridCategoryFilterProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const { resetGrid } = useGrid();
  const [isOpen, setIsOpen] = React.useState(false);
  const panelId = React.useId();

  const visibleCategories = categories.filter(
    (category) => category.name !== "Unkategorisiert",
  );

  if (visibleCategories.length === 0) {
    return null;
  }

  const handleCategoryChange = (categoryId: number | null) => {
    resetGrid();
    onCategoryChange(categoryId);
  };

  const filterLabel = getLabel("categories", "Kategorien");
  const setCategory = (categoryId: number | null) => {
    if (categoryId === activeCategoryId) {
      return;
    }

    handleCategoryChange(categoryId);
  };

  return (
    <div
      data-is-open={isOpen}
      className={removeExtraSpacesFromClassNames(
        `${styles["grid-category-filter"]} ${className}`,
      )}
    >
      <button
        type="button"
        className={styles["filter-toggle"]}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((wasOpen) => !wasOpen)}
      >
        {isOpen ? (
          <CustomSVG
            src={getIcon("filter_close")}
            wrapper="span"
            width={24}
            height={24}
            aria-hidden={true}
            className={styles["toggle-icon"]}
          />
        ) : (
          <CustomSVG
            iconKey={"filter_open"}
            wrapper="span"
            width={24}
            height={24}
            aria-hidden={true}
            className={styles["toggle-icon"]}
            fallback={
              <span
                className={styles["filter-icon-fallback"]}
                aria-hidden={true}
              />
            }
          />
        )}
        <span>{filterLabel}</span>
      </button>
      <div
        className={styles["category-pills-wrapper"]}
        data-is-open={isOpen}
        aria-hidden={!isOpen}
        id={panelId}
      >
        <div className={styles["category-pills"]}>
          <CategoryPill
            isActive={activeCategoryId === null}
            label={getLabel("glossary_all_lable", "Alle")}
            onClick={() => setCategory(null)}
            tabIndex={isOpen ? 0 : -1}
          />
          {visibleCategories.map((category) => (
            <CategoryPill
              key={category.id}
              isActive={activeCategoryId === category.id}
              label={category.name}
              onClick={() => setCategory(category.id)}
              tabIndex={isOpen ? 0 : -1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface CategoryPillProps {
  isActive: boolean;
  label: string;
  onClick: () => void;
  tabIndex: number;
}

const CategoryPill = ({
  isActive,
  label,
  onClick,
  tabIndex,
}: CategoryPillProps) => {
  return (
    <button
      type="button"
      className={styles["category-pill"]}
      data-is-active={isActive}
      aria-pressed={isActive}
      onClick={onClick}
      tabIndex={tabIndex}
    >
      <CustomSVG
        iconKey="active_filter_arrow"
        wrapper="span"
        width={24}
        height={24}
        aria-hidden={true}
        className={styles["category-pill-icon"]}
      />
      <span
        className={styles["category-pill-label"]}
        dangerouslySetInnerHTML={{
          __html: customSanitize(label),
        }}
      />
    </button>
  );
};

export default GridCategoryFilter;
